<template>
  <div class="learn-search-container">
    <CategoryList />

    <listSortOptions
      v-if="searchText && searchText.length > 0"
      class="pdr-8 pdb-18 d-flex justify-content-end"
      :sortOptions="sortOptions"
      @searchSort="searchSort"
    />

    <div class="learn-search-wrapper">
      <!-- 최근 검색 이력 (최근 검색 이력 있으면 && 검색 결과 데이터 없으면) -->
      <div
        class="recent-search-wrapper"
        v-if="
          recentSearchList && recentSearchList.length > 0 && !searchDataList
        "
      >
        <div class="search-title">{{ $t("3918") }}</div>
        <div class="mgt-16" />
        <div
          v-for="recentData in recentSearchList"
          :key="recentData.id"
          class="search-text-wrapper cursor-pointer"
          @click="clickSearch(recentData.searchText)"
        >
          <div class="search-icon-head">
            <span class="material-icons search-item-text">history</span>
          </div>
          <div class="search-text">{{ recentData.searchText }}</div>
          <div
            class="search-icon-tail"
            @click="(e) => deleteRecentSearchList(e, recentData)"
          >
            <span class="material-icons search-item-text">close</span>
          </div>
        </div>
      </div>

      <!-- 검색 결과 (검색 결과 데이터 있으면) -->
      <div
        class="result-list-wrapper"
        v-if="searchDataList && searchDataList.length > 0"
      >
        <div v-for="(item, i) in searchDataList" :key="i">
          <ListItem
            :degree="item"
            :status="item.progressViewStatus"
            @initProgressStatus="initProgressStatus"
          />
        </div>

        <InfiniteLoading
          @infinite="moreData"
          :identifier="infiniteId"
          spinner="spiral"
        >
          <div slot="no-more" class="mgt-16">No more data</div>
          <div slot="no-results" class="mgt-16"></div>
        </InfiniteLoading>
      </div>

      <div v-if="isNoData" class="mgt-200 t-center">
        <div class="t-center t-headline8 font-r">{{ $t("3919") }}</div>
        <div class="mgt-4 t-center t-body1 t-color-bodyGrayA">
          {{ $t("3920") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import InfiniteLoading from "vue-infinite-loading";
import learnApi from "@/api/learnApi";
import { getSortOptions } from "@/utils/sortOptionsUtil";

export default {
  name: "LearnSearch",
  components: {
    InfiniteLoading,
    CategoryList: () => import("./CategoryList.vue"),
    listSortOptions: () =>
      import("@/components/common/RightListSortOptions.vue"),
    ListItem: () => import("../ListItem.vue"),
    NoData: () => import("@/components/common/NoData.vue"),
  },
  data() {
    return {
      isNoData: false,

      // 검색 데이터 목록
      searchDataList: null,

      // 최근 검색어 목록
      recentSearchList: null,

      // 정렬 조건
      sortOptions: getSortOptions(this.$route.query.status),
      sortOption: getSortOptions(this.$route.query.status)[0].sortValue,

      totalPage: 1,
      currentPage: 1,
      infiniteId: +new Date(),
      moreLoading: false,
      status: "",
    };
  },
  computed: {
    ...mapState({
      searchText: (state) => state.learn.searchText,
      categoryState: (state) => state.learn.categoryState,
    }),
  },
  watch: {
    searchText(value) {
      if (value === "") {
        this.currentPage = 1;
        this.totalPages = 1;
        this.infiniteId += 1;
        this.searchDataList = null;
        this.isNoData = false;
        this.searchSort(getSortOptions(this.$route.query.status)[0].sortValue);
      } else {
        this.currentPage = 1;
        this.search();
      }
    },
    categoryState() {
      if (this.searchText === "") return;
      this.initData();
      this.search();
    },
    sortOption() {
      this.initData();
      this.search();
    },
  },

  mounted() {
    this.getRecentSearchList();
  },

  destroyed() {
    this.SET_SEARCH_TEXT("");
    this.SET_CATEGORY_STATE(null);
  },

  methods: {
    ...mapActions("learn", ["SET_SEARCH_TEXT", "SET_CATEGORY_STATE"]),

    initData() {
      this.listData = [];
      this.currentPage = 1;
      this.totalPages = 1;
      this.infiniteId += 1;
    },

    setParams() {
      const params = {
        status: this.$route.query.status,
        search: this.searchText,
        categories: this.categoryState ? this.categoryState.value : "",
        page: this.currentPage,
        size: 10,
        sort: this.sortOption,
      };
      return params;
    },

    async search(categoryText) {
      this.$showLoading();
      if (this.searchText === "") {
        this.$hideLoading();
        this.searchDataList = null;
        return;
      }

      const params = this.setParams();
      try {
        const searchResult = await learnApi.fetchList(params);
        if (searchResult && searchResult.data.data) {
          this.searchDataList = searchResult.data.data.content.map((val) => {
            val.progressViewStatus = false;
            return val;
          });
          this.totalPage = searchResult.data.data.totalPages;
          if (categoryText !== "category") {
            this.addRecentSearchList();
          }
        }
        if (this.searchDataList.length > 0) {
          this.isNoData = false;
        } else {
          this.isNoData = true;
        }
      } catch (error) {
        console.log("SEARCH ERROR : ", error);
      } finally {
        this.$hideLoading();
      }
    },

    getRecentSearchList() {
      const localRecentSearchList = JSON.parse(
        localStorage.getItem("recentSearchList")
      );
      if (localRecentSearchList) {
        this.recentSearchList = [...localRecentSearchList].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      }
    },

    addRecentSearchList() {
      if (!this.recentSearchList) {
        this.recentSearchList = [];
      }

      const isDuplicate =
        Array.isArray(this.recentSearchList) &&
        this.recentSearchList.some(
          (item) => item.searchText === this.searchText
        );

      if (!isDuplicate) {
        const typedArray = new Uint8Array(1);
        const randomValue = crypto.getRandomValues(typedArray)[0];
        const randomFloat = randomValue / Math.pow(2, 8);
        const addSearchData = {
          searchText: this.searchText,
          createdAt: new Date(),
          id: Math.floor(randomFloat * 900000) + 100000,
        };

        if (this.recentSearchList.length === 10) {
          this.recentSearchList.pop();
        }
        this.recentSearchList.unshift(addSearchData);
        localStorage.setItem(
          "recentSearchList",
          JSON.stringify(this.recentSearchList)
        );
      }
    },

    clickSearch(word) {
      this.currentPage = 1;
      this.SET_SEARCH_TEXT(word);
    },

    deleteRecentSearchList(e, data) {
      e.stopPropagation();
      const filterRecentSearchList = this.recentSearchList.filter(
        (item) => item.id !== data.id
      );
      localStorage.setItem(
        "recentSearchList",
        JSON.stringify(filterRecentSearchList)
      );
      this.getRecentSearchList();
    },

    async moreData($state) {
      if (this.moreLoading) {
        return;
      }

      this.moreLoading = true;
      this.currentPage += 1;
      this.infiniteId += 1;
      const params = this.setParams();
      try {
        const searchResult = await learnApi.fetchList(params);
        if (searchResult && searchResult.data.data) {
          this.searchDataList = [
            ...this.searchDataList,
            ...searchResult.data.data.content,
          ];
        }

        if (searchResult.data.data.content.length > 0) {
          $state.loaded();
        } else {
          $state.complete();
        }
      } catch (error) {
        console.log("MORE SEARCH ERROR : ", error);
      }

      this.moreLoading = false;
    },

    initProgressStatus(id) {
      this.searchDataList = this.searchDataList.map((o) => {
        if (o.id === id) {
          if (o.progressViewStatus) {
            o.progressViewStatus = false;
            o.status = false;
          } else {
            o.progressViewStatus = true;
            o.status = true;
          }
          console.log(o.progressViewStatus);
        }
        return o;
      });
    },

    searchSort(value) {
      this.sortOptions.map((sort) => {
        sort.isShow = sort.sortValue === value;
      });
      this.sortOption = value;
    },
  },
};
</script>

<style scoped>
.learn-search-container {
  padding: 16px 0;
}
.learn-search-wrapper {
  margin-top: 16px;
}
.recent-search-wrapper {
  padding: 0 16px;
}

.search-icon-tail {
  margin-left: auto;
  color: var(--Iconinactive);
}
.search-text {
  font-size: 14px;
  line-height: 22px;
  font-weight: 500;
  margin-left: 8px;
  color: var(--HeadlineBlack);
}
.search-text-wrapper {
  display: flex;
  padding: 13px 0px;
}
.search-icon-head {
  color: var(--Iconinactive);
}
.right4 {
  top: 50px !important;
}
</style>